import React, { useState, useEffect } from "react";
import { UploadDropZone } from "./dropzone";
import { Typography, Box, IconButton, Paper } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { UploadDropZoneProps } from "./utils";
import { StyledButton } from "../buttons/custom-button";
import { ReactComponent as Loader } from "../../../assests/logos/loader.svg";
import { atcService } from "../../../screens/alternative-transaction-certificates/atc-service/atc.service";
import useNotification from "../../hooks/useNotification";

interface FileUploaderProps {
  uniqueId: string;
  setFileName: (fileNames: string[]) => void;
  setFilePath: (filePaths: string[]) => void;
  onUploadSuccess?: (fileInfo: { fileName: string; file: File }) => void;
  onUploadError?: (error: string) => void;
  atcNo: string;
  atcVersionNo: string;
  disabled?: boolean;
  uploadedFile?: File | null;
}

const FileUploader = ({
  uniqueId,
  setFileName,
  setFilePath,
  onUploadSuccess,
  onUploadError,
  atcNo,
  atcVersionNo,
  disabled = false,
  uploadedFile,
}: FileUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const { notify } = useNotification();

  // Sync selectedFile with uploadedFile prop on mount or when it changes
  useEffect(() => {
    if (uploadedFile) {
      setSelectedFile(uploadedFile);
      setIsFileUploaded(true); // Mark as uploaded if prop provides a file
    } else {
      setSelectedFile(null);
      setIsFileUploaded(false); // Reset upload status if no file
    }
  }, [uploadedFile]);

  // Validate file type (PDF only)
  const validateFileType = (file: File): boolean => {
    const allowedTypes = ["application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setError("Please upload only PDF (.pdf) files");
      return false;
    }
    return true;
  };

  // Upload file to backend
  const uploadFileToBackend = async (file: File): Promise<boolean> => {
    try {
      const sanitizeFilename = (name: string): string =>
        name.replace(/[^\w.-]/g, "_");

      const safeFilename = sanitizeFilename(file.name);
      const response = await atcService.uploadFile(file, {
        filename: safeFilename,
        atcNo,
        atcVersionNo,
      });

      if (response?.status === 200) {
        return true;
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("File upload error:", error);
      return false;
    }
  };

  const handleFileUpload: UploadDropZoneProps["onClick"] = (event) => {
    if (disabled) return;
    const { files: rawFiles, error: fileError } = event.target;
    if (fileError) {
      setError(fileError);
      return;
    }

    const files = rawFiles as FileList;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFileType(file)) {
        setSelectedFile(file);
        setError("");
        setIsFileUploaded(false); // Allow re-upload when a new file is selected
      }
    }
  };

  const handleUploadClick = async () => {
    if (disabled || !selectedFile) {
      setError(disabled ? "Form is not ready for upload" : "No file selected for upload.");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const uploadSuccess = await uploadFileToBackend(selectedFile);
      if (uploadSuccess) {
        setFileName([selectedFile.name]);
        setFilePath([selectedFile.name]);
        onUploadSuccess?.({ fileName: selectedFile.name, file: selectedFile });
        setIsFileUploaded(true); // Mark as uploaded
      } else {
        setError("File upload failed");
        onUploadError?.("File upload failed");
      }
    } catch (error) {
      const errorMessage = `Upload failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`;
      setError(errorMessage);
      onUploadError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    if (disabled) return;
    setSelectedFile(null);
    setError("");
    setIsFileUploaded(false); // Reset upload status to allow new upload
    const fileInput = document.querySelector(
      `#${uniqueId}-upload-zone input[type="file"]`
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        border: "1px solid #ddd",
        borderRadius: 2,
        p: 3,
        maxWidth: "600px",
        margin: "0 auto",
        backgroundColor: "#fafafa",
      }}
    >
      {isUploading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "250px",
            flexDirection: "column",
          }}
        >
          <Loader style={{ width: 50, height: 50 }} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Uploading file...
          </Typography>
        </Box>
      ) : (
        <>
          <UploadDropZone
            onClick={handleFileUpload}
            label="Upload your file"
            id={`${uniqueId}-upload-zone`}
            dataTestId="file-uploader-dropzone"
            acceptFiles={["pdf"]}
            maxSize="10MB"
            multiple={false}
            fileRange={1}
            allowDuplication={false}
            acceptMultiDot={true}
            error={Boolean(error)}
            removeText="file"
            disabled={disabled || isFileUploaded} // Disable drop zone only if disabled or uploaded
          />

          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 2, textAlign: "center" }}
            >
              {error}
            </Typography>
          )}

          {selectedFile && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Selected File:</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                <Typography variant="body2">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  disabled={isUploading || disabled} // Enable delete unless uploading or parent-disabled
                  sx={{
                    color: "#d32f2f",
                    "&:hover": {
                      backgroundColor: "#ffebee",
                      color: "#b71c1c",
                    },
                    "&:disabled": {
                      color: "#bdbdbd",
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleUploadClick}
              disabled={!selectedFile || isUploading || disabled || isFileUploaded} // Disable upload button after upload
            >
              Upload File
            </StyledButton>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default FileUploader;  